import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import moment, { Moment } from 'moment';
import { Gym, MembershipType } from 'src/app/model/gym';
import { AttributeChangeSchedule } from 'src/app/model/schedule/attribute-change-schedule';
import {
  MAX_CREDITS_PRICE,
  MembershipTypeScheduleUtils,
  MembershipTypeTarget,
  MIN_CREDITS_PRICE,
} from 'src/app/util/membership-type-schedule-util';
import { DATE_FORMATS } from '../add-attribute-change-schedule-dialog/add-attribute-change-schedule-dialog.component';

export type PromoteMembershipTypeDialogData = {
  gym: Gym;
  target: MembershipTypeTarget;
};

const MEMBERSHIP_TYPE_LABELS: Record<MembershipType, string> = {
  [MembershipType.UNLIMITED]: 'Unlimited',
  [MembershipType.CREDITS]: 'Credits',
  [MembershipType.PLUS_1]: 'Plus 1',
};

@Component({
  selector: 'gymfinder-promote-membership-type-dialog',
  templateUrl: './promote-membership-type-dialog.component.html',
  styleUrls: ['./promote-membership-type-dialog.component.scss'],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS }],
  standalone: false,
})
export class PromoteMembershipTypeDialogComponent implements OnInit {
  readonly MembershipType = MembershipType;
  readonly MIN_PRICE = MIN_CREDITS_PRICE;
  readonly MAX_PRICE = MAX_CREDITS_PRICE;

  gym: Gym;
  target: MembershipTypeTarget;

  currentTypes: MembershipType[] = [];
  targetTypes: MembershipType[] = [];
  currentPrices: number[] = [];
  hasConflict = false;
  hasPlus1 = false;
  alreadyInTarget = false;

  price?: number;
  scheduledAt?: Moment;
  minDate: Date;

  constructor(
    private dialogRef: MatDialogRef<PromoteMembershipTypeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: PromoteMembershipTypeDialogData
  ) {
    this.gym = data.gym;
    this.target = data.target;
  }

  ngOnInit(): void {
    const now = new Date();
    this.minDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1
    );
    this.scheduledAt = moment(this.minDate);

    this.currentTypes = MembershipTypeScheduleUtils.currentMembershipTypes(
      this.gym
    );
    this.targetTypes = MembershipTypeScheduleUtils.targetMembershipTypes(
      this.gym,
      this.target
    );
    this.currentPrices = MembershipTypeScheduleUtils.currentPrices(this.gym);
    this.hasConflict = MembershipTypeScheduleUtils.hasConflict(this.gym);
    this.hasPlus1 = MembershipTypeScheduleUtils.hasPlus1(this.gym);
    this.alreadyInTarget =
      this.currentTypes.includes(this.target) && !this.hasConflict;

    const existingPrice = this.currentPrices[0];
    if (
      this.isCreditsTarget &&
      MembershipTypeScheduleUtils.isValidPrice(existingPrice)
    ) {
      this.price = existingPrice;
    }
  }

  public get isCreditsTarget(): boolean {
    return this.target === MembershipType.CREDITS;
  }

  public get targetLabel(): string {
    return this.label(this.target);
  }

  public label(type: MembershipType): string {
    return MEMBERSHIP_TYPE_LABELS[type] ?? type;
  }

  public isRemoved(type: MembershipType): boolean {
    return !this.targetTypes.includes(type);
  }

  public isAdded(type: MembershipType): boolean {
    return !this.currentTypes.includes(type);
  }

  public get canSubmit(): boolean {
    return (
      !!this.scheduledAt &&
      (!this.isCreditsTarget ||
        MembershipTypeScheduleUtils.isValidPrice(this.price))
    );
  }

  public submit() {
    if (!this.canSubmit || !this.scheduledAt) {
      return;
    }
    const schedule: AttributeChangeSchedule =
      MembershipTypeScheduleUtils.buildSchedule(
        this.gym.id,
        this.target,
        this.scheduledAt.toDate().toISOString(),
        this.price
      );
    this.dialogRef.close(schedule);
  }

  public closeDialog() {
    this.dialogRef.close(undefined);
  }
}
